import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { loadAppConfig } from "./appConfig.ts";
import { isNativeApp } from "./native.ts";
import { useSession } from "./session.ts";
import { getSocket } from "./socket.ts";

const KEY = "mk.ads.completed.v1";
const AD_TIMEOUT = 5_000;
const IDS_CAP = 50;
const ads = registerPlugin<{
  prepare(): Promise<void>;
  disable(): Promise<void>;
  showIfReady(): Promise<{ shown: boolean }>;
  privacyOptions(): Promise<void>;
  addListener(event: "adShowing", listener: () => void): Promise<PluginListenerHandle>;
}>("Ads");

interface Counter { ids: string[]; count: number; pendingId: string | null }
const EMPTY: Counter = { ids: [], count: 0, pendingId: null };
let enabled = false;
let showing: Promise<void> | null = null;

export function nextCounter(current: Counter, id: string): Counter {
  if (current.ids.includes(id)) return current;
  const count = current.count + 1;
  const ids = [...current.ids, id].slice(-IDS_CAP);
  return { ids, count, pendingId: count % 2 === 0 ? id : null };
}

function readCounter(): Counter {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (parsed && typeof parsed === "object") {
      const value = parsed as Partial<Counter>;
      if (Array.isArray(value.ids) && value.ids.every((id) => typeof id === "string") &&
        Number.isSafeInteger(value.count) && (value.count ?? -1) >= 0 &&
        (value.pendingId === null || typeof value.pendingId === "string")) return value as Counter;
    }
  } catch { /* No storage: ads fail closed. */ }
  return EMPTY;
}

function writeCounter(counter: Counter): void {
  try { localStorage.setItem(KEY, JSON.stringify(counter)); }
  catch { /* No persistent count: skip rather than show out of order. */ }
}

export async function configureAds(): Promise<void> {
  if (!isNativeApp()) return;
  const session = useSession.getState();
  const config = await loadAppConfig();
  const canShow = Boolean(config.liveAdsEnabled) &&
    (session.status === "anon" || session.status === "authed" && session.user?.removeAds === false);
  enabled = canShow;
  if (canShow) await ads.prepare().catch(() => { enabled = false; });
  else {
    const current = readCounter();
    if (current.pendingId) writeCounter({ ...current, pendingId: null });
    await ads.disable().catch(() => {});
  }
}

export function recordCompletedGame(id: string): void {
  if (!enabled || !isNativeApp() || !id) return;
  writeCounter(nextCounter(readCounter(), id));
}

/**
 * Waits for the native ad to finish. The timeout only guards against an ad that
 * never appears: once it is on screen we wait for the player to close it, so the
 * next game (a 3-minute run's clock, a rematch, matchmaking) never starts behind it.
 */
export function waitForAd(show: Promise<unknown>, started: Promise<void>, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    let onScreen = false;
    const timer = setTimeout(() => { if (!onScreen) resolve(); }, timeoutMs);
    void started.then(() => { onScreen = true; });
    void show.then(() => {}, () => {}).finally(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/** Consume the second-game chance before opening the native ad. Never replay it later. */
export function leaveResultThroughAd(): Promise<void> {
  if (showing) return showing;
  if (!enabled || !isNativeApp()) return Promise.resolve();
  const current = readCounter();
  if (!current.pendingId) return Promise.resolve();
  writeCounter({ ...current, pendingId: null });
  if (!navigator.onLine) return Promise.resolve();
  const socket = useSession.getState().status === "authed" ? getSocket() : null;
  socket?.emit("ad:busy", { busy: true });
  let markShowing = () => {};
  const started = new Promise<void>((resolve) => { markShowing = resolve; });
  // Listen before asking for the ad so the "on screen" signal cannot be missed.
  const listener: Promise<PluginListenerHandle | null> =
    ads.addListener("adShowing", () => markShowing()).catch(() => null);
  const show = listener.then(() => ads.showIfReady());
  showing = waitForAd(show, started, AD_TIMEOUT).finally(() => {
    void listener.then((handle) => handle?.remove());
    socket?.emit("ad:busy", { busy: false });
    showing = null;
  });
  return showing;
}

export async function openAdPrivacyOptions(): Promise<void> {
  if (isNativeApp()) await ads.privacyOptions();
}
